import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Alert,
  FormControlLabel,
  Switch,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  Divider,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import { motion } from "framer-motion";
import { Permission, Role } from "./types/permissions";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import CustomTextField from "../../../components/ui/Textfield";
import PermissionReviewScreen from "./PermissionReviewScreen";
import PermissionAppList from "./PermissionAppList";
import { usePermissionsPicker } from "./usePermissionsPicker";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";

interface PermissionsManagementProps {
  role: Role;
  allPermissions: Permission[];
  onBack: () => void;
  onUpdatePermissions: (roleId: number, permissions: number[]) => void;
}

const PermissionsManagement: React.FC<PermissionsManagementProps> = ({
  role,
  allPermissions,
  onBack,
  onUpdatePermissions,
}) => {
  const { hasPermission } = usePermissions();
  const canEditPermissions = hasPermission(PERMISSIONS.USER_MANAGEMENT_CHANGE);
  const [granted, setGranted] = useState<Set<number>>(
    new Set(role.permissions),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [mode, setMode] = useState<"edit" | "review">("edit");

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const {
    searchTerm,
    setSearchTerm,
    expandedApps,
    toggleApp,
    showSystemApps,
    setShowSystemApps,
    showEventModels,
    setShowEventModels,
    appFilter,
    setAppFilter,
    groupedByApp,
    visibleGrouped,
    hiddenCounts,
    togglePermission,
    toggleAllPermissionsForApp,
    toggleFullCrudForModel,
    isAppFullyGranted,
    isAppPartiallyGranted,
    handleBulk,
    stats,
  } = usePermissionsPicker(allPermissions, granted, setGranted);

  const hasUnsavedChanges = useMemo(() => {
    const originalSet = new Set(role.permissions);
    if (originalSet.size !== granted.size) return true;
    for (const id of originalSet) {
      if (!granted.has(id)) return true;
    }
    return false;
  }, [granted, role.permissions]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onUpdatePermissions(role.id, Array.from(granted));
    } finally {
      setIsSaving(false);
    }
  };

  if (!Array.isArray(allPermissions) || allPermissions.length === 0) {
    return (
      <Box p={3}>
        <Typography color="error">No permissions available</Typography>
      </Box>
    );
  }

  if (mode === "review") {
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
      >
        <PermissionReviewScreen
          role={role}
          groupedByApp={groupedByApp}
          granted={granted}
          stats={stats}
          isSaving={isSaving}
          onBack={() => setMode("edit")}
          onConfirm={handleSave}
        />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        display="flex"
        gap={3}
        alignItems="flex-start"
        flexWrap={{ xs: "wrap", lg: "nowrap" }}
      >
        <Box flex={1} minWidth={0}>
          <Paper
            sx={{
              boxShadow: 0,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              p: 3,
              mb: 3,
            }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton
                onClick={onBack}
                sx={{ border: "1px solid", borderColor: "divider" }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Box flexGrow={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h5" fontWeight="bold">
                    {role.name}
                  </Typography>
                  <Chip
                    label={role.is_managed ? "Managed" : "Custom"}
                    size="small"
                    color={role.is_managed ? "warning" : "default"}
                    variant="outlined"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {typeof role.user_count === "number"
                    ? `${role.user_count} assigned user${role.user_count !== 1 ? "s" : ""}`
                    : "Manage the permissions assigned to this role"}
                </Typography>
              </Box>
            </Box>

            {role.is_managed && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <strong>{role.name}</strong> is centrally managed in code (
                <code>group_permissions.py</code>). Any changes saved here will
                be overwritten the next time the permission matrix is deployed (
                <code>sync_groups</code>). Edit the matrix in code for changes
                that need to persist.
              </Alert>
            )}
          </Paper>

          <Paper
            sx={{
              boxShadow: 0,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              p: 2,
              mb: 3,
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              flexWrap="wrap"
              mb={2}
            >
              <CustomTextField
                placeholder="Search permissions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{ flexGrow: 1, minWidth: 200 }}
              />
              <ToggleButtonGroup
                value={appFilter}
                exclusive
                size="small"
                onChange={(_, v) => v && setAppFilter(v)}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="selected">Selected</ToggleButton>
                <ToggleButton value="unselected">Unselected</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box
              display="flex"
              alignItems="center"
              gap={2}
              flexWrap="wrap"
              mb={1.5}
            >
              <Tooltip
                title={`${hiddenCounts.systemApps} permissions hidden from internal Django/library apps (admin, contenttypes, pghistory, etc.)`}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={showSystemApps}
                      onChange={(e) => setShowSystemApps(e.target.checked)}
                      size="small"
                    />
                  }
                  label={`Show system apps${hiddenCounts.systemApps ? ` (${hiddenCounts.systemApps})` : ""}`}
                />
              </Tooltip>
              <Tooltip
                title={`${hiddenCounts.eventModels} audit-trail permissions hidden (auto-generated history/event twins of each model)`}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={showEventModels}
                      onChange={(e) => setShowEventModels(e.target.checked)}
                      size="small"
                    />
                  }
                  label={`Show audit trail models${hiddenCounts.eventModels ? ` (${hiddenCounts.eventModels})` : ""}`}
                />
              </Tooltip>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography
                variant="caption"
                fontWeight="bold"
                color="text.secondary"
                sx={{ mr: 0.5 }}
              >
                BULK (VISIBLE):
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleBulk("selectAll")}
              >
                Select All
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleBulk("clearAll")}
              >
                Clear All
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleBulk("readOnly")}
              >
                Read Only
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="success"
                onClick={() => handleBulk("fullCrud")}
              >
                Full CRUD
              </Button>
            </Box>
          </Paper>

          <PermissionAppList
            visibleGrouped={visibleGrouped}
            granted={granted}
            expandedApps={expandedApps}
            toggleApp={toggleApp}
            toggleAllPermissionsForApp={toggleAllPermissionsForApp}
            toggleFullCrudForModel={toggleFullCrudForModel}
            togglePermission={togglePermission}
            isAppFullyGranted={isAppFullyGranted}
            isAppPartiallyGranted={isAppPartiallyGranted}
            accentColor={reduxTheme.primary_colors[400]}
          />
        </Box>

        <Paper
          sx={{
            boxShadow: 0,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            width: { xs: "100%", lg: 300 },
            position: { lg: "sticky" },
            top: { lg: 16 },
            p: 2.5,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            Role Summary
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mb={2}
          >
            Live role configuration
          </Typography>

          <Box display="flex" flexDirection="column" gap={2}>
            <StatRow
              icon={<GroupsOutlinedIcon fontSize="small" />}
              value={stats.appsSelected}
              label="Applications"
              caption="Accessible by this role"
            />
            <StatRow
              icon={<AppsOutlinedIcon fontSize="small" />}
              value={stats.modelsSelected}
              label="Models"
              caption="With at least one permission"
            />
            <StatRow
              icon={<VerifiedOutlinedIcon fontSize="small" />}
              value={stats.permissionsSelected + stats.customSelected}
              label="Total Permissions"
              caption={`${stats.customSelected} custom`}
            />
          </Box>

          {canEditPermissions && (
            <Box display="flex" flexDirection="column" gap={1} mt={2.5}>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={isSaving || !hasUnsavedChanges}
                sx={{ backgroundColor: reduxTheme.primary_colors[400] }}
              >
                {isSaving ? "Saving..." : "Save changes"}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </motion.div>
  );
};

const StatRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number;
  caption: string;
}> = ({ icon, label, value, caption }) => (
  <Box display="flex" alignItems="center" gap={1.5}>
    <Avatar
      variant="rounded"
      sx={{
        width: 32,
        height: 32,
        bgcolor: "action.selected",
        color: "text.secondary",
      }}
    >
      {icon}
    </Avatar>
    <Box>
      <Typography variant="body2" fontWeight="bold">
        {value} {label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
    </Box>
  </Box>
);

export default PermissionsManagement;
